import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { isInvalidIxdStructureError } from "@/apis/errors";
import { postCheckIxdGuidelineWithPreview } from "@/apis/guideline/route";
import type { GuidelineCheckWithPreviewResponse } from "@/apis/guideline/types";
import { IxdUploadNotice } from "@/components/ixd-upload-notice";
import { UploadFailedModal } from "@/components/modals/upload-failed-modal";
import { pushModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import { GUIDELINE_RESULT_READY, GUIDELINE_RESULT_DATA } from "./result";

// ---------------------------------------------------------------------------
// 핸드셰이크: 새 탭(result 페이지)이 READY 를 보내거나 결과가 먼저 도착하면
// 양쪽 모두 준비된 시점에 데이터를 전달한다(둘 중 늦게 오는 쪽 기준).
// ---------------------------------------------------------------------------

interface PendingHandshake {
  win: Window;
  data: GuidelineCheckWithPreviewResponse;
  filename: string;
}

function sendResult(
  win: Window,
  data: GuidelineCheckWithPreviewResponse,
  filename: string,
) {
  win.postMessage(
    { type: GUIDELINE_RESULT_DATA, data, filename },
    window.location.origin,
  );
}

export const GuidelineCheckPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // 새 탭 창 ref — 버튼 클릭 시 저장, mutation 완료 시 메시지 전송에 사용
  const resultWinRef = useRef<Window | null>(null);
  // 결과 대기 버퍼: 탭이 READY를 보내기 전에 결과가 먼저 오면 여기에 보관
  const pendingRef = useRef<PendingHandshake | null>(null);
  // ready 리스너 cleanup ref
  const readyListenerRef = useRef<((e: MessageEvent) => void) | null>(null);

  const mutation = useMutation({
    mutationFn: (file: File) => postCheckIxdGuidelineWithPreview(file),
    onSuccess: (data, file) => {
      const win = resultWinRef.current;
      if (!win || win.closed) {
        toast.error("결과 탭이 닫혔습니다. 다시 시도해주세요.");
        return;
      }
      const filename = file.name;

      // 이미 READY 리스너가 등록 해제되었다면(탭이 먼저 READY 전송 완료) 바로 전송
      if (readyListenerRef.current === null) {
        sendResult(win, data, filename);
      } else {
        // READY를 아직 못 받음 — 버퍼에 보관, 리스너가 받으면 처리
        pendingRef.current = { win, data, filename };
      }
    },
    onError: (error: unknown) => {
      if (isInvalidIxdStructureError(error)) {
        pushModal(UploadFailedModal);
        return;
      }
      const detail =
        (error as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ??
        (error as Error)?.message ??
        "요청 처리 중 오류가 발생했습니다.";
      toast.error(detail);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    mutation.reset();
  };

  const handleRun = () => {
    if (!selectedFile) return;

    // 1) 유저 제스처 안에서 즉시 새 탭을 연다(팝업 차단 회피)
    const win = window.open("/guideline-check/result", "_blank");
    if (!win) {
      toast.error("팝업이 차단되었습니다. 팝업을 허용한 뒤 다시 시도해주세요.");
      return;
    }
    resultWinRef.current = win;
    pendingRef.current = null;

    // 기존 리스너 정리
    if (readyListenerRef.current) {
      window.removeEventListener("message", readyListenerRef.current);
    }

    // 2) READY 리스너 등록
    const onReady = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== win) return;
      if ((event.data as { type?: string })?.type !== GUIDELINE_RESULT_READY)
        return;

      // 리스너 정리
      window.removeEventListener("message", onReady);
      readyListenerRef.current = null;

      // 버퍼에 결과가 이미 와 있으면 바로 전송
      const pending = pendingRef.current;
      if (pending) {
        sendResult(pending.win, pending.data, pending.filename);
        pendingRef.current = null;
      }
      // 결과가 아직 없으면 mutation.onSuccess 에서 처리
    };
    readyListenerRef.current = onReady;
    window.addEventListener("message", onReady);

    // 3) mutation 실행
    mutation.mutate(selectedFile);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-neutral-50 via-white to-neutral-100">
      <header className="flex h-14 px-6 items-center gap-4 border-b border-neutral-200 bg-white/80 backdrop-blur-sm">
        <button
          type="button"
          title="뒤로 가기"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-semibold text-neutral-950">
          IXD 가이드라인 검증
        </h1>
      </header>

      <div className="flex-1 flex flex-col items-center p-8">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-neutral-900 mb-3">
              IXD 파일을 업로드하세요
            </h2>
            <p className="text-neutral-600">
              금융감독원 가이드라인 준수 여부를 검사하고 리포트를 반환합니다.
            </p>
          </div>

          <div
            className="border-2 border-dashed border-neutral-300 rounded-xl p-10 text-center cursor-pointer hover:border-primary-base transition-colors mb-6"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-10 h-10 mx-auto mb-3 text-neutral-400" />
            {selectedFile ? (
              <p className="text-neutral-800 font-medium">
                {selectedFile.name}
              </p>
            ) : (
              <p className="text-neutral-500">
                IXD 파일(.ixd)을 클릭하여 선택하세요
              </p>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".ixd"
              aria-label="IXD 파일 선택"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          <IxdUploadNotice className="my-4" />

          <Button
            onClick={handleRun}
            disabled={!selectedFile || mutation.isPending}
            className="w-full"
          >
            {mutation.isPending ? "검사 중..." : "가이드라인 검사 실행"}
          </Button>

          {mutation.isPending && (
            <p className="mt-4 text-center text-sm text-neutral-500">
              검사 결과가 새 탭에서 열립니다.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
